import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ContactNumberFormGroup,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { TypedFormGroup } from '@principle-theorem/ng-shared';
import { Brand, ReferralSource } from '@principle-theorem/principle-core';
import {
  IDVACard,
  IHealthFundCard,
  IMedicareCard,
  IPatient,
  IReferralSource,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, multiMap, WithRef } from '@principle-theorem/shared';
import { asapScheduler, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { NATIONAL_HEALTH_INDEX_NUMBER_LENGTH } from '../patient-details-form/national-health-index-number-form/national-health-index-number-form.component';
import { PatientDetailsFormComponent } from '../patient-details-form/patient-details-form.component';
import {
  BasePatientField,
  ICompareFormFieldState,
  PatientDetailsFormComparisonService,
  PatientHealthCardField,
} from './patient-details-form-comparison.service';

@Component({
  selector: 'pr-patient-details-form-compare',
  templateUrl: './patient-details-form-compare.component.html',
  styleUrl: './patient-details-form-compare.component.scss',
  providers: [PatientDetailsFormComparisonService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientDetailsFormCompareComponent extends PatientDetailsFormComponent {
  patient$ = new ReplaySubject<WithRef<IPatient>>(1);
  referrers$: Observable<IReferralSource[]>;

  @Input()
  set patient(patient: WithRef<IPatient>) {
    if (patient) {
      this.patient$.next(patient);
    }
  }

  @Input({ transform: coerceBooleanProperty })
  set disabled(disabled: boolean) {
    this.service.disabled$.next(disabled);
  }

  constructor(
    public service: PatientDetailsFormComparisonService,
    private _org: OrganisationService
  ) {
    super();

    this.showSchoolField$ = this.patient$.pipe(
      map(
        (patient) =>
          !!patient.metadata?.moh?.school ||
          this._isUnder18(patient.dateOfBirth)
      )
    );

    service.init(this.patient$, this.patientDetails$, this.form);

    this.referrers$ = this._org.brand$.pipe(
      filterUndefined(),
      switchMap((brand) => Brand.referralSources$(brand)),
      multiMap((source) => ReferralSource.toReferrer(source))
    );

    combineLatest([
      this.service.disabled$,
      this.patientDetails$.pipe(startWith(undefined)),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([disabled]) => this._applyDisabledState(disabled));

    // Syncs at the end of the turn, not after the base form's 500 ms debounce,
    // so Confirm saves what is shown but skips the half-written values date
    // and address inputs report while controls are patched or unlocked.
    this.form.valueChanges
      .pipe(debounceTime(0, asapScheduler), takeUntilDestroyed())
      .subscribe(() => this.submit());
  }

  /**
   * An incomplete typed number marks the form invalid so Confirm cannot save
   * the previous number. Stored numbers of any length are never re-validated,
   * which is why this sets an error instead of adding a validator.
   */
  nationalHealthIndexNumberTyped(
    nationalHealthIndexNumber: string | undefined
  ): void {
    const control = this.form.controls.nationalHealthIndexNumber;
    control.setValue(nationalHealthIndexNumber);
    if (
      !nationalHealthIndexNumber ||
      nationalHealthIndexNumber.length >= NATIONAL_HEALTH_INDEX_NUMBER_LENGTH
    ) {
      return;
    }
    control.setErrors({ incompleteNationalHealthIndexNumber: true });
  }

  getBasePatientFieldState$(
    fieldName: keyof BasePatientField,
    subFieldName?: string
  ): Observable<ICompareFormFieldState | undefined> {
    return this.service.getBasePatientFieldState$(fieldName, subFieldName);
  }

  getPatientNameFieldState$(
    fieldName: 'firstName' | 'lastName'
  ): Observable<ICompareFormFieldState | undefined> {
    return this.service.getPatientNameFieldState$(fieldName);
  }

  getBasePatientNestedFieldState$(
    args: [keyof BasePatientField, string?]
  ): Observable<ICompareFormFieldState | undefined> {
    return this.getBasePatientFieldState$(...args);
  }

  getContactNumberFieldState$(
    formGroup: ContactNumberFormGroup,
    index: number
  ): Observable<ICompareFormFieldState | undefined> {
    return this.service.getContactNumberFieldState$(formGroup, index);
  }

  getContactNumberFieldStateByIndex$(
    args: [ContactNumberFormGroup, number]
  ): Observable<ICompareFormFieldState | undefined> {
    return this.getContactNumberFieldState$(...args);
  }

  getHealthCardFieldState$(
    formGroup: TypedFormGroup<IHealthFundCard | IMedicareCard | IDVACard>,
    fieldName: keyof PatientHealthCardField
  ): Observable<ICompareFormFieldState | undefined> {
    return this.service.getHealthCardFieldState$(formGroup, fieldName);
  }

  isSameReferrer(
    aReferrer?: IReferralSource,
    bReferrer?: IReferralSource
  ): boolean {
    if (!aReferrer && !bReferrer) {
      return true;
    }
    if (!aReferrer || !bReferrer) {
      return false;
    }
    return aReferrer.name === bReferrer.name;
  }

  getSchoolFieldState$(): Observable<ICompareFormFieldState | undefined> {
    return this.service.getSchoolFieldState$();
  }

  /**
   * Toggles only on a real change, so re-showing an editable form does not make
   * date and address inputs report. Locking then re-syncs the group: an input
   * reporting mid-lock leaves it VALID with an empty value.
   */
  private _applyDisabledState(disabled: boolean): void {
    if (this._isInDisabledState(disabled)) {
      return;
    }
    if (disabled) {
      this.form.disable({ emitEvent: false });
      this.form.updateValueAndValidity({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
  }

  /**
   * Checks every control: the group's own `disabled` is skewed by one enabled
   * child (a rebuilt contact number) or value-accessor feedback while locking.
   */
  private _isInDisabledState(disabled: boolean): boolean {
    return Object.values(this.form.controls).every(
      (control) => control.disabled === disabled
    );
  }
}
